import React, { useMemo, useState } from 'react'
import { SearchOption } from '@/components/ui-primitives/search-dropdown'
import { Button } from '@/components/ui-primitives/small'
import { ViewerConfig } from "@/components/Viewer"
import { WidgetConfig } from '@/components/WidgetConfiguration'
import {
  determineWidgetPath, parentWidgetPath,
  getWidgetIcon,
  intelligentlyDecideEndLabel,
  intelligentlyDecideStartLabel,
  widgetPathToIndices,
} from '@/components/editor/widget-tree'
import { useWidgetSelectorStore } from '@/components/editor/widget-selector/widget-selector'
import { ArrowUp, ChevronDown, ChevronLeft, ChevronRight, Eye, PenLine, SquareMousePointer } from 'lucide-react'
import { mergeClasses } from '@/helpers/styling'
import { revealSignal } from '@/components/editor/widget-editor/reveal'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-primitives/popover'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui-primitives/command'
import { DynamicIcon } from '@/components/DynamicIcon'
import { ACCENT } from '@/components/editor/accent'
import { editIdentity } from '@/components/editor/widget-editor/editor-tabs'

export function WidgetSwitcher(props: {
  viewerConfig: ViewerConfig
  configDraft: WidgetConfig | null
  isPicking: boolean
  onTogglePicking: () => void
}) {
  const selectedPath = useWidgetSelectorStore(s => s.selectedPath)
  const parentPath = selectedPath ? parentWidgetPath(selectedPath) : null
  const [ isOpen, setIsOpen ] = useState(false)

  const widgetOptions = useMemo(() => {
    return collectWidgetOptions(props.viewerConfig.mainArea, props.viewerConfig.mainArea)
  }, [props.viewerConfig])

  const currentIndex = useMemo(() => {
    if (!selectedPath) return -1
    return widgetOptions.findIndex(widget => widget.value === selectedPath)
  }, [ widgetOptions, selectedPath ])

  const parentIndex = useMemo(() => {
    if (!parentPath) return -1
    return widgetOptions.findIndex(widget => widget.value === parentPath)
  }, [ widgetOptions, parentPath ])

  const select = (path: string | undefined) => {
    if (!path) return
    useWidgetSelectorStore.setState({ selectedPath: path })
  }

  const reveal = () => {
    if (!selectedPath) return
    revealSignal.emit({ path: selectedPath, mainArea: props.viewerConfig.mainArea })
  }

  return (
    // sticky, with the tab bar below it, while the settings scroll
    <div className="sticky top-0 z-20 flex items-center gap-0.5 h-12 px-2 border-b border-border overflow-hidden bg-white0">
      <Button variant="ghost"
              title="Previous widget"
              onClick={() => select(widgetOptions[currentIndex - 1]?.value)}
              disabled={!selectedPath || currentIndex <= 0}>
        <ChevronLeft size={14} />
      </Button>
      <Button variant="ghost"
              title="Next widget"
              onClick={() => select(widgetOptions[currentIndex + 1]?.value)}
              disabled={!selectedPath || currentIndex === widgetOptions.length - 1}>
        <ChevronRight size={14} />
      </Button>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button type="button"
                  className="flex flex-1 min-w-[7rem] items-center gap-2 h-9 px-2 rounded-md text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {props.configDraft ? (
              <>
                <span className={mergeClasses("flex size-[26px] shrink-0 items-center justify-center rounded-md", ACCENT.iconTile)}>
                  <DynamicIcon icon={getWidgetIcon(props.configDraft)} size="14px" />
                </span>
                <span className="font-exo text-base font-semibold truncate">{props.configDraft.widget}</span>
                {props.configDraft.id && (
                  <span className="font-mono text-xs text-muted-foreground truncate">#{props.configDraft.id}</span>
                )}
              </>
            ) : (
              <span className="text-sm text-muted-foreground truncate">
                {selectedPath ? "Empty area" : "Select a widget…"}
              </span>
            )}
            <ChevronDown size={14} className="ml-auto shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-80">
          <Command>
            <CommandInput placeholder="Search widgets on this page…" />
            <CommandList>
              <CommandEmpty>No widgets configured yet</CommandEmpty>
              {widgetOptions.map(option => (
                <CommandItem key={option.value}
                             value={`${option.label} ${option.value}`}
                             onSelect={() => {
                               select(option.value)
                               setIsOpen(false)
                             }}
                             className={mergeClasses("gap-2 text-xs", {
                               [ACCENT.selectedItem]: option.value === selectedPath,
                             })}
                             style={{ paddingLeft: 8 + option.indent }}>
                  {option.icon && <DynamicIcon icon={option.icon} size="14px" />}
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {props.configDraft && (
        <Button variant="ghost"
                title="Edit type & identifier"
                onClick={editIdentity}>
          <PenLine size={14} />
        </Button>
      )}
      <Button variant="ghost"
              title="Parent widget"
              onClick={() => select(widgetOptions[parentIndex]?.value)}
              disabled={parentIndex === -1}>
        <ArrowUp size={14} />
      </Button>
      <Button variant="ghost"
              title="Reveal widget"
              onClick={reveal}
              disabled={!selectedPath}>
        <Eye size={14} />
      </Button>
      <Button variant="ghost"
              title={props.isPicking ? "Stop picking" : "Pick widget on page"}
              className={mergeClasses({ 'ring-2 animate-pulse': props.isPicking })}
              onClick={props.onTogglePicking}>
        <SquareMousePointer size={14} />
      </Button>
    </div>
  )
}

type WidgetOption = SearchOption & { indent: number }

function collectWidgetOptions(mainArea: WidgetConfig, area: WidgetConfig, parent?: WidgetConfig | null): WidgetOption[] {
  const childAreas = area.areas ?? []
  return [
    toOption(area, parent ?? null),
    ...childAreas
      .filter(childArea => !!childArea)
      .flatMap(childArea => collectWidgetOptions(mainArea, childArea!, area)) // Claude: possible to remove the ! maybe?
      .filter(opt => !!opt.value)
  ] as WidgetOption[]

  function toOption(area: WidgetConfig, parent: WidgetConfig | null) {
    const path = determineWidgetPath(area, mainArea)
    return {
      value: path,
      label: `${intelligentlyDecideStartLabel(area, parent)} (${intelligentlyDecideEndLabel(area, parent)})`,
      icon: getWidgetIcon(area),
      indent: path ? widgetPathToIndices(path).length * 6 : 0
    }
  }
}
